import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';
import { SAUDI_CITIES, getDistrictsForCity } from '../../lib/saudi-locations';

interface Alert {
  id: string;
  propertyType: string | null;
  purpose: string | null;
  city: string | null;
  district: string | null;
  minPrice: number | string | null;
  maxPrice: number | string | null;
  notifySms: boolean;
  notifyEmail: boolean;
  active: boolean;
  lastTriggered: string | null;
  createdAt: string;
}

export function MyAlertsPage() {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['my-alerts'],
    queryFn: () => api.get<ApiSuccess<Alert[]>>('/api/alerts/me'),
  });

  const toggleM = useMutation({
    mutationFn: (id: string) => api.put(`/api/alerts/${id}/toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-alerts'] }),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/alerts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-alerts'] }),
  });

  const list = data?.data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark mb-1">🔔 تنبيهات البحث</h1>
          <p className="text-ink-secondary">احفظ معايير بحثك وسنخبرك فور توفر عقار مطابق</p>
        </div>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ تنبيه جديد</Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-ink-muted">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card><CardBody>
          <div className="text-center py-16">
            <div className="text-7xl mb-4">🔕</div>
            <h3 className="text-xl font-black text-navy-dark mb-2">لا توجد تنبيهات</h3>
            <p className="text-sm text-ink-muted mb-4">أنشئ تنبيهك الأول لتصلك إشعارات فور توفر عقار يطابق معاييرك</p>
            <Button variant="primary" onClick={() => setOpenCreate(true)}>+ إنشاء تنبيه</Button>
          </div>
        </CardBody></Card>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <Card key={a.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge color={a.active ? 'green' : 'red'}>{a.active ? '🟢 نشط' : '⏸ موقوف'}</Badge>
                      {a.lastTriggered && (
                        <span className="text-xs text-ink-muted">
                          آخر تنبيه: {new Date(a.lastTriggered).toLocaleDateString('ar-SA')}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap text-sm">
                      {a.propertyType && <Tag>{a.propertyType}</Tag>}
                      {a.purpose && <Tag>{a.purpose === 'sale' ? 'للبيع' : 'للإيجار'}</Tag>}
                      {a.city && <Tag>📍 {a.city}</Tag>}
                      {a.district && <Tag>{a.district}</Tag>}
                      {(a.minPrice || a.maxPrice) && (
                        <Tag>
                          {a.minPrice ? `من ${Number(a.minPrice).toLocaleString('en-US')}` : ''}
                          {a.minPrice && a.maxPrice ? ' — ' : ''}
                          {a.maxPrice ? `إلى ${Number(a.maxPrice).toLocaleString('en-US')}` : ''}
                          {' ر.س'}
                        </Tag>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2 text-xs text-ink-muted">
                      {a.notifyEmail && <span>📧 إيميل</span>}
                      {a.notifySms && <span>📱 SMS</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant={a.active ? 'ghost' : 'primary'} onClick={() => toggleM.mutate(a.id)}>
                      {a.active ? '⏸' : '▶'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف التنبيه؟')) deleteM.mutate(a.id); }}>🗑</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {openCreate && <CreateAlertModal onClose={() => setOpenCreate(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['my-alerts'] })} />}
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return <span className="bg-navy-soft text-navy-dark text-xs font-bold rounded-lg px-2 py-1">{children}</span>;
}

function CreateAlertModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    propertyType: '', purpose: '', city: '', district: '',
    minPrice: '', maxPrice: '',
    notifyEmail: true, notifySms: true,
  });
  const districts = [...new Set(getDistrictsForCity(f.city))];
  const m = useMutation({
    mutationFn: () => api.post('/api/alerts', {
      ...f,
      propertyType: f.propertyType || null,
      purpose: f.purpose || null,
      city: f.city || null,
      district: f.district || null,
      minPrice: f.minPrice ? Number(f.minPrice) : null,
      maxPrice: f.maxPrice ? Number(f.maxPrice) : null,
    }),
    onSuccess: () => { onSaved(); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">🔔 تنبيه بحث جديد</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-bold mb-1">نوع العقار</label>
            <select value={f.propertyType} onChange={(e) => setF({ ...f, propertyType: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="">الكل</option>
              <option value="APARTMENT">شقة</option>
              <option value="VILLA">فيلا</option>
              <option value="LAND">أرض</option>
              <option value="OFFICE">مكتب</option>
              <option value="SHOP">محل</option>
              <option value="WAREHOUSE">مستودع</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">الغرض</label>
            <select value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="">الكل</option>
              <option value="sale">للبيع</option>
              <option value="rent">للإيجار</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">المدينة</label>
            <select value={f.city} onChange={(e) => setF({ ...f, city: e.target.value, district: '' })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="">كل المدن</option>
              {SAUDI_CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">الحي</label>
            <select value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} disabled={!f.city} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white disabled:bg-gray-100">
              <option value="">{f.city ? 'كل أحياء المدينة' : 'اختر المدينة أولاً'}</option>
              {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <Input label="الحد الأدنى للسعر" type="number" value={f.minPrice} onChange={(e) => setF({ ...f, minPrice: e.target.value })} hint="ر.س — اختياري" />
          <Input label="الحد الأعلى للسعر" type="number" value={f.maxPrice} onChange={(e) => setF({ ...f, maxPrice: e.target.value })} hint="ر.س — اختياري" />
          <div className="md:col-span-2 space-y-2 pt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.notifyEmail} onChange={(e) => setF({ ...f, notifyEmail: e.target.checked })} className="w-5 h-5 accent-navy" /><span className="text-sm font-bold">📧 إشعار بالإيميل</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.notifySms} onChange={(e) => setF({ ...f, notifySms: e.target.checked })} className="w-5 h-5 accent-navy" /><span className="text-sm font-bold">📱 إشعار بـ SMS</span></label>
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>🔔 تفعيل التنبيه</Button>
        </div>
      </div>
    </div>
  );
}
